import React, { useMemo, useState } from 'react';
import { Grid, CircularProgress, IconButton } from '@material-ui/core';
import { Close, PictureAsPdf } from '@material-ui/icons';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import FileDropZone from '@/components/OrderDetailsPage/FileDropZone';
import ImageCarousel from '@/components/OrderDetailsPage/ImageCarousel';
import uploadPhotoAsync from '@/utilities/uploadPhotoAsync';
import { Body2 } from '../blocks';

const isPdf = (url = '') => url.split('?')[0].toLowerCase().endsWith('.pdf');

// Accept legacy bare-string entries alongside the { url, environment } shape.
const normalize = (photos) =>
    (photos || []).map((p) => (typeof p === 'string' ? { url: p } : p)).filter((p) => p?.url);

const THUMB = css`
    width: 100px;
    height: 100px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid ${colors.greys.border};
    cursor: pointer;
`;

// One photo set (thumbnails + dropzone + delete + lightbox), persisted by the parent via onChange.
const PhotoSection = ({ title, hint, photos, editable, folder, category, onChange, onError }) => {
    const list = useMemo(() => normalize(photos), [photos]);
    const [uploading, setUploading] = useState(false);
    const [carousel, setCarousel] = useState({ open: false, index: 0 });

    // Only images participate in the lightbox; PDFs open in a new tab.
    const imageList = useMemo(() => list.filter((p) => !isPdf(p.url)), [list]);

    const handleFileAdd = async (files) => {
        setUploading(true);
        try {
            const uploaded = [];
            for (const { file, fileType } of files) {
                const objectUrl = URL.createObjectURL(file);
                const url = await uploadPhotoAsync(objectUrl, category, fileType, folder);
                URL.revokeObjectURL(objectUrl);
                if (url) uploaded.push({ url, environment: 'web' });
            }
            if (uploaded.length) onChange([...list, ...uploaded]);
        } catch (err) {
            onError?.(err);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = (index) => onChange(list.filter((_, i) => i !== index));

    return (
        <div
            css={css`
                margin-bottom: 1.5rem;
            `}
        >
            <Body2>{title}</Body2>
            {hint ? (
                <div
                    css={css`
                        color: ${colors.greys.secondary};
                        font-size: 13px;
                        margin: 2px 0 10px;
                    `}
                >
                    {hint}
                </div>
            ) : null}
            <Grid
                container
                direction="row"
                css={css`
                    gap: 12px;
                    flex-wrap: wrap;
                    align-items: center;
                    margin-top: 8px;
                `}
            >
                {list.map((photo, index) => (
                    <div
                        key={photo.url}
                        css={css`
                            position: relative;
                        `}
                    >
                        {isPdf(photo.url) ? (
                            <a
                                href={photo.url}
                                target="_blank"
                                rel="noreferrer"
                                css={css`
                                    width: 100px;
                                    height: 100px;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    border: 1px solid ${colors.greys.border};
                                    border-radius: 4px;
                                    background: #f4f4f4;
                                `}
                            >
                                <PictureAsPdf
                                    css={css`
                                        color: ${colors.greys.primary};
                                    `}
                                />
                            </a>
                        ) : (
                            <img
                                src={photo.url}
                                alt={title}
                                css={THUMB}
                                onClick={() =>
                                    setCarousel({
                                        open: true,
                                        index: Math.max(
                                            0,
                                            imageList.findIndex((p) => p.url === photo.url)
                                        ),
                                    })
                                }
                            />
                        )}
                        {editable ? (
                            <IconButton
                                size="small"
                                onClick={() => handleDelete(index)}
                                css={css`
                                    position: absolute;
                                    top: -10px;
                                    right: -10px;
                                    background: white;
                                    &:hover {
                                        background: white;
                                    }
                                `}
                            >
                                <Close
                                    fontSize="small"
                                    css={css`
                                        color: ${colors.reds.primary};
                                    `}
                                />
                            </IconButton>
                        ) : null}
                    </div>
                ))}
                {uploading ? <CircularProgress size={28} /> : null}
                {editable ? (
                    <FileDropZone
                        type="multi"
                        handleFileAdd={handleFileAdd}
                        disabled={uploading}
                        customLabel="Drag and Drop or Select"
                    />
                ) : null}
                {!editable && list.length === 0 ? (
                    <span
                        css={css`
                            color: ${colors.greys.secondary};
                            font-size: 13px;
                        `}
                    >
                        No photos uploaded.
                    </span>
                ) : null}
            </Grid>

            <ImageCarousel
                images={imageList}
                initialIndex={carousel.index}
                open={carousel.open}
                onClose={() => setCarousel({ open: false, index: 0 })}
            />
        </div>
    );
};

export default PhotoSection;
